import { useState } from 'react';
import { Search, MapPin, Briefcase, Trophy, DollarSign, SortAsc } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SearchFilters } from '@/utils/optimizedQueries';

interface InstantSearchBarProps {
  filters: SearchFilters;
  onFiltersChange: (filters: Partial<SearchFilters>) => void;
  isLoading?: boolean;
}

const locations = [
  'all', 'Colombo', 'Kandy', 'Galle', 'Jaffna', 'Negombo', 
  'Matara', 'Ratnapura', 'Batticaloa', 'Kurunegala', 'Anuradhapura'
];

const jobTypes = [
  'all', 'full-time', 'part-time', 'contract', 'freelance', 'internship'
];

const experienceLevels = [
  'all', 'entry', 'mid', 'senior', 'executive'
];

const sortOptions = [
  { value: 'relevance', label: 'Relevance', icon: Search },
  { value: 'newest', label: 'Newest First', icon: SortAsc },
  { value: 'salary', label: 'Highest Salary', icon: DollarSign },
];

export const InstantSearchBar = ({ filters, onFiltersChange, isLoading }: InstantSearchBarProps) => {
  const [localQuery, setLocalQuery] = useState(filters.query || '');

  const handleQueryChange = (value: string) => {
    setLocalQuery(value);
    onFiltersChange({ query: value });
  };

  const handleRemoteToggle = () => {
    onFiltersChange({ is_remote: !filters.is_remote });
  };

  return (
    <div className="space-y-4">
      {/* Main search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input
          type="text"
          placeholder="Search jobs, companies, or keywords..."
          value={localQuery}
          onChange={(e) => handleQueryChange(e.target.value)}
          className="pl-10 pr-4 h-12 text-base"
          disabled={isLoading}
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
          </div>
        )}
      </div>

      {/* Filters row */}
      <div className="flex flex-wrap gap-3 items-center">
        {/* Location */}
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          <Select 
            value={filters.location || 'all'} 
            onValueChange={(value) => onFiltersChange({ location: value })}
          >
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Location" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {locations.slice(1).map(location => (
                <SelectItem key={location} value={location}>{location}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Job Type */}
        <div className="flex items-center gap-2">
          <Briefcase className="h-4 w-4 text-muted-foreground" />
          <Select 
            value={filters.job_type || 'all'} 
            onValueChange={(value) => onFiltersChange({ job_type: value })}
          >
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Job Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {jobTypes.slice(1).map(type => (
                <SelectItem key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Experience Level */}
        <div className="flex items-center gap-2">
          <Trophy className="h-4 w-4 text-muted-foreground" />
          <Select 
            value={filters.experience_level || 'all'} 
            onValueChange={(value) => onFiltersChange({ experience_level: value })}
          >
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Levels</SelectItem>
              {experienceLevels.slice(1).map(level => (
                <SelectItem key={level} value={level}>
                  {level.charAt(0).toUpperCase() + level.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Remote toggle */}
        <Button
          variant={filters.is_remote ? "default" : "outline"}
          size="sm"
          onClick={handleRemoteToggle}
          className="h-9"
        >
          Remote Only
        </Button>

        {/* Sort */}
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-sm text-muted-foreground">Sort by:</span>
          <Select 
            value={filters.sort_by || 'relevance'} 
            onValueChange={(value) => onFiltersChange({ sort_by: value as SearchFilters['sort_by'] })}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              {sortOptions.map(option => {
                const Icon = option.icon;
                return (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4" />
                      {option.label}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Results info */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <div>
          {filters.query && (
            <span>Search results for "{filters.query}"</span>
          )}
        </div>
        <div className="flex items-center gap-4">
          {isLoading && (
            <Badge variant="outline" className="animate-pulse">
              Searching...
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
};